import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import { PageHead } from '../reporting/PageHead';
import { Panel } from '../dashboard/ui';
import { downloadCsv, niceDate, num, type TeamUser } from '../lib/reporting';

export function Users() {
  const [users, setUsers] = useState<TeamUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let active = true;
    apiFetch<TeamUser[]>('/api/reports/users')
      .then((u) => {
        if (active) setUsers(Array.isArray(u) ? u : []);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load users');
      });
    return () => {
      active = false;
    };
  }, []);

  const name = (u: TeamUser) => [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';
  const filtered = useMemo(() => {
    if (!users) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) =>
      [name(u), u.email, u.role, u.company].some((v) => (v ?? '').toLowerCase().includes(needle)),
    );
  }, [users, q]);

  const exportCsv = () => {
    if (!users) return;
    downloadCsv(
      'nvs-team-users.csv',
      filtered.map((u) => ({
        name: name(u),
        email: u.email ?? '',
        role: u.role ?? '',
        company: u.company ?? '',
        created: u.created_date ?? '',
      })),
    );
  };

  return (
    <section className="report">
      <PageHead
        crumb="Team / Users"
        title="Users"
        subtitle={users ? `${num(users.length)} team members` : 'Team members'}
      />

      {error && <p className="form-error" role="alert">{error}</p>}
      {!users && !error && <p className="muted">Loading…</p>}

      {users && (
        <Panel
          title="Team directory"
          subtitle={`${num(filtered.length)} shown`}
          action={<button type="button" className="ghost-btn" onClick={exportCsv}>Export CSV</button>}
        >
          <div className="users-search">
            <input
              type="search"
              placeholder="Search name, email, role or company…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Company</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.user_id}>
                    <td>{name(u)}</td>
                    <td className="muted">{u.email ?? '—'}</td>
                    <td>{u.role ?? '—'}</td>
                    <td className="muted">{u.company ?? '—'}</td>
                    <td>{u.created_date ? niceDate(u.created_date.slice(0, 10)) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </section>
  );
}
